/** S·C·G의 공통 계산. 좌표 텍스트와 3D 마커가 같은 결과를 사용한다. */
import type { HandRole } from "../domain/mapping";
import type { ViewerTrack } from "../domain/viewerTypes";
import { addVectors, gripFromClosed, rotateVector } from "./handKinematics";
import { openingAt } from "./gripperOpening";
import type { PoseWxyz } from "./poseMath";
import { samplePoseAt } from "./poseSampling";
import {
  eulerMatrix,
  handMatrix,
  matrixToQuaternionWxyz,
  multiplyMatrices,
  quaternionToMatrix,
  type Matrix3,
  type Vec3,
} from "./rotationMath";

export type TcpInspection = {
  readonly trackId: string;
  readonly seconds: number;
  readonly axis: "X" | "Y" | "Z";
  readonly degrees: number;
};

/** 근거와 별도로 고정 변환의 계산 가능성을 판단한다. null은 0이 아니다. */
export function tcpOffset(role: HandRole): Vec3 | null {
  const { origin, transform, applied_correction: applied } = role.tcp;
  if (
    role.pose_kind !== "absolute" ||
    ("motion" in role && (role.motion as { kind: string | null }).kind !== "absolute")
  )
    return null;
  if (origin.point === "sigmakit_closed_tcp") {
    return transform.kind === "identity" &&
      (applied.state === "native_target" || applied.state === "applied_to_target")
      ? [0, 0, 0]
      : null;
  }
  if (origin.point !== "flange" && origin.point !== "other_fixed") return null;
  if (origin.point === "other_fixed" && origin.definition === null) return null;
  if (transform.kind !== "fixed" || transform.axes !== "source_tool") return null;
  if (applied.state !== "not_applied" && applied.state !== "other_applied") return null;
  if (applied.state === "other_applied" && applied.description === null) return null;
  const { x_mm: x, y_mm: y, z_mm: z } = transform;
  return x === null || y === null || z === null || ![x, y, z].every(Number.isFinite)
    ? null
    : [x / 1000, y / 1000, z / 1000];
}

export type TcpPoints = {
  readonly source: Vec3;
  readonly closed: Vec3 | null;
  readonly grip: Vec3 | null;
  readonly handPose: PoseWxyz | null;
};

/** d에는 손 축 보정 H를 적용하지 않는다. C = S + Rs·d, RH = Rs·H. */
export function tcpPoints(
  source: Vec3,
  sourceRotation: Matrix3,
  role: HandRole,
  opening: number | null,
): TcpPoints {
  const d = tcpOffset(role);
  const h = handMatrix(role.rotation);
  if (d === null) return { source, closed: null, grip: null, handPose: null };
  const closed = addVectors(source, rotateVector(sourceRotation, d));
  if (h === null) return { source, closed, grip: null, handPose: null };
  const rh = multiplyMatrices(sourceRotation, h);
  return {
    source,
    closed,
    grip: opening === null ? null : addVectors(closed, rotateVector(rh, gripFromClosed(opening))),
    handPose: [...closed, ...matrixToQuaternionWxyz(rh)],
  };
}

/** 점검 회전은 원본 도구 축에서 Rs 뒤에 곱한다. S와 표본 시각은 고정한다. */
export function sampleTcp(
  track: ViewerTrack,
  seconds: number,
  inspection: TcpInspection | null = null,
): TcpPoints | null {
  if (track.tcp === undefined) return null;
  const frozen = inspection?.trackId === track.id ? inspection : null;
  const time = frozen?.seconds ?? seconds;
  const { pose } = samplePoseAt(track.tcp.source, time);
  if (pose === null) return null;
  let rotation = quaternionToMatrix([pose[3], pose[4], pose[5], pose[6]]);
  if (frozen !== null) {
    const radians = (frozen.degrees * Math.PI) / 180;
    const angles: Vec3 = [
      frozen.axis === "X" ? radians : 0,
      frozen.axis === "Y" ? radians : 0,
      frozen.axis === "Z" ? radians : 0,
    ];
    rotation = multiplyMatrices(rotation, eulerMatrix(angles, "XYZ", true));
  }
  return tcpPoints(
    [pose[0], pose[1], pose[2]],
    rotation,
    track.tcp.role,
    track.gripper ? openingAt(track.gripper, time) : null,
  );
}
